// The bcfRouterRouteMatcher function is responsible for matching the url with the routes.
// It sorts the routes by path length and then iterates over them.
// It uses the regexparam library to parse the path and create a regex.
// It also handles the wildcard and the not found route.
// It returns the resolved route with the params.
import { isDefined } from '@bcf-vanilla-ts-v1-shared/misc/pure-utils/is-defined';
import { parse } from 'regexparam';
import type { BcfRoute, Params, ResolvedBcfRoute, UrlMatchResult, UrlSegment } from './types';

type ParseRegexResult = {
  keys: string[];
  pattern: RegExp;
};

function exec(path: string, result: ParseRegexResult): Params {
  const matches: RegExpExecArray | null = result.pattern.exec(path);
  const out: Params = {};

  if (!matches) {
    // If no match, return all keys with null
    for (const key of result.keys) {
      out[key] = null;
    }
    return out;
  }

  for (let i: number = 0; i < result.keys.length; i++) {
    // i+1 because match[0] is the full match
    if (result.keys[i] !== '*') {
      out[result.keys[i]] = matches[i + 1] ?? null;
    }
  }

  return out;
}

function normalizeUrl(url: string): string {
  if (url === '') {
    return '/';
  }
  return url.startsWith('/') ? url : `/${url}`;
}

/** Empty-path redirects must not match child segments (e.g. `/profile` when only `/` should redirect). */
function isEmptyPathRedirectMatch(urlToMatch: string): boolean {
  const url: string = normalizeUrl(urlToMatch);
  return url === '/' || url === '';
}

function sortRoutesByPathLength(appRoutes: BcfRoute[]): BcfRoute[] {
  return appRoutes
    .slice()
    .filter((route: BcfRoute) => isDefined(route.path))
    .sort((a: BcfRoute, b: BcfRoute) => {
      // Treat wildcard '*' as slightly less specific than a normal character for sorting purposes.
      const pathA: string = a.path!.replace(/\*/g, '!');
      const pathB: string = b.path!.replace(/\*/g, '!');
      return pathB.length - pathA.length;
    });
}

function isMatchForAsterix(route: BcfRoute, inputUrl: string): boolean {
  const emptyPath: string | undefined = route.fallthroughAvailablePaths!.find((path: string) => path === '');
  const nonEmptyPath: string[] = route.fallthroughAvailablePaths!.filter((path: string) => path !== '');

  if (emptyPath === '' && inputUrl === '/') {
    return true;
  }
  return nonEmptyPath.some((path: string) => inputUrl.includes(path));
}

function addBaseRoutePathToRoutes(addPath: string, routes: BcfRoute[]): BcfRoute[] {
  const normalizePath = (routePath: string): string => {
    let newAddPath: string = addPath;
    let newRoutePath: string = routePath;
    if (newAddPath.endsWith('*')) {
      newAddPath = newAddPath.slice(0, -1);
    }
    if (newAddPath.endsWith('/')) {
      newAddPath = newAddPath.slice(0, -1);
    }
    if (newRoutePath.startsWith('/')) {
      newRoutePath = newRoutePath.slice(1);
    }
    return `${newAddPath}/${newRoutePath}`;
  };

  return routes.map((route: BcfRoute) => {
    return {
      ...route,
      path: normalizePath(route.path!)
    };
  });
}

function generateRouteData(route: BcfRoute, routeParams: Params): Record<string, any> | undefined {
  let container: Record<string, any> = {};
  if (route.data) {
    container = {
      ...route.data
    };
  }
  if (route.dataGen && routeParams) {
    container = {
      ...container,
      ...route.dataGen(routeParams)
    };
  }

  return Object.keys(container).length === 0 ? undefined : container;
}

export function bcfRouterRouteMatcher(appRoutes: BcfRoute[], inputUrl: string): ResolvedBcfRoute | undefined {
  const sortedAppRoutes: BcfRoute[] = sortRoutesByPathLength(appRoutes);

  const notFoundRoute: BcfRoute | undefined = sortedAppRoutes.find((route: BcfRoute) => route.path === '**');
  const otherRoutes: BcfRoute[] = sortedAppRoutes.filter((route: BcfRoute) => route.path !== '**');
  const routerWithMatcher: BcfRoute[] = appRoutes.filter((route: BcfRoute) => Boolean(route.matcher));

  const segments: UrlSegment[] = inputUrl
    .split('/')
    .filter((segment: string) => segment.length > 0)
    .map((segment: string) => ({
      path: segment
    }));

  for (const route of routerWithMatcher) {
    if (route.matcher) {
      const result: UrlMatchResult | undefined = route.matcher(segments, route);
      if (result) {
        return {
          route: result.route,
          params: result.params,
          renderCSRDelay: route.renderCSRDelay,
          data: generateRouteData(route, result.params)
        };
      }
    }
  }

  for (const route of otherRoutes) {
    if (!isDefined(route.path)) {
      continue;
    }
    const pathRegex: ParseRegexResult = parse(route.path);
    const urlToMatch: string = inputUrl;
    const match: boolean = pathRegex.pattern.test(urlToMatch);

    if (match && route.path === '*') {
      if (!route.fallthroughAvailablePaths) {
        throw Error("Missing variable fallthroughAvailablePaths for '*' route ");
      }
      const isMatch: boolean = isMatchForAsterix(route, inputUrl);
      if (!isMatch) {
        continue;
      }
    }

    if (match) {
      const result: Params = exec(urlToMatch, pathRegex);

      if (route.redirectTo) {
        if (route.path === '' && !isEmptyPathRedirectMatch(urlToMatch)) {
          continue;
        }
        const redirectUrl: string = route.redirectTo.replace(/:([^/]+)/g, (_match: string, key: string) => {
          const value: unknown = result[key];
          return value != null ? String(value) : `:${key}`;
        });
        return {
          route: route,
          params: result,
          renderCSRDelay: route.renderCSRDelay,
          data: generateRouteData(route, result),
          urlToNavigate: redirectUrl.replace(/\/+/g, '/')
        };
      }

      if (!route.render && route.loadRoutes) {
        return bcfRouterRouteMatcher(addBaseRoutePathToRoutes(route.path, route.loadRoutes()), inputUrl);
      }

      return {
        route: route,
        params: result,
        renderCSRDelay: route.renderCSRDelay,
        data: generateRouteData(route, result)
      };
    }
  }

  if (notFoundRoute) {
    return {
      route: notFoundRoute,
      params: {}
    };
  }

  return undefined;
}
